/**
 * CompactCharacterCount.tsx
 *
 * Compact character count display for EditorToolbar.
 * Shows character count with expandable dropdown for detailed platform breakdown.
 *
 * Features:
 * - Compact display: "156/280 ▼"
 * - Color-coded based on usage (green, yellow, red)
 * - Radix Popover dropdown with full breakdown
 * - Per-platform status with icons
 * - Progress bar visualization
 * - Only visible when content exists
 */

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getRoot } from 'lexical';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Popover from '@radix-ui/react-popover';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import {
  checkLimitsWithCounting,
  calculatePercentage,
  getPercentageColorClass,
  getProgressBarColorClass,
  formatNumber,
  getPlatformLimit,
  getPlatformDisplayName,
} from '@/constants/platformLimits';
import {
  countCharacters,
  countCharactersForPlatform,
  countWords,
} from '@/utils/characterCount';
import { countHashtags } from '@/utils/hashtags';

interface CompactCharacterCountProps {
  /**
   * Selected platforms to show counts for
   */
  platforms?: string[];

  /**
   * Show separator before the count
   */
  showSeparator?: boolean;
}

/**
 * Progress bar component for character usage
 */
function ProgressBar({ percentage }: { percentage: number }) {
  const colorClass = getProgressBarColorClass(percentage);
  const clampedPercentage = Math.min(percentage, 100);

  return (
    <div className="h-1.5 w-full bg-gray-200 rounded-full overflow-hidden">
      <div
        className={`h-full transition-all duration-200 ${colorClass}`}
        style={{ width: `${clampedPercentage}%` }}
      />
    </div>
  );
}

/**
 * Platform status row component
 */
function PlatformStatusRow({
  platform,
  text,
}: {
  platform: string;
  text: string;
}) {
  const limit = getPlatformLimit(platform, 'characters');
  const displayName = getPlatformDisplayName(platform);
  const platformCount = countCharactersForPlatform(text, platform);
  const isOver = platformCount > limit;

  return (
    <div className="flex items-center justify-between text-sm py-1.5">
      <div className="flex items-center gap-2">
        {isOver ? (
          <Icon name={ICONS.CAUTION} size="sm" color="text-danger" />
        ) : (
          <Icon name={ICONS.TICK_CIRCLE} size="sm" color="text-success" />
        )}
        <span className="font-medium text-gray-700">{displayName}</span>
      </div>
      <span className={`text-xs ${isOver ? 'text-danger' : 'text-gray-600'}`}>
        {formatNumber(platformCount)} / {formatNumber(limit)}
      </span>
    </div>
  );
}

/**
 * CompactCharacterCount component
 * Displays compact character count in toolbar with expandable dropdown
 */
export function CompactCharacterCount({
  platforms = [],
  showSeparator = false,
}: CompactCharacterCountProps): JSX.Element | null {
  const { t } = useTranslation('common');
  const [editor] = useLexicalComposerContext();
  const [characterCount, setCharacterCount] = useState(0);
  const [wordCount, setWordCount] = useState(0);
  const [hashtagCount, setHashtagCount] = useState(0);
  const [textContent, setTextContent] = useState('');
  const [open, setOpen] = useState(false);

  // Subscribe to editor updates
  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const root = $getRoot();
        const text = root.getTextContent();

        const count = countCharacters(text);
        const words = countWords(text);
        const hashtags = countHashtags(text);

        setTextContent(text);
        setCharacterCount(count);
        setWordCount(words);
        setHashtagCount(hashtags);
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  // Calculate limit check results (memoized so unrelated re-renders of this
  // component don't re-trigger the per-platform character counting/segmentation)
  const limitCheck = useMemo(
    () => checkLimitsWithCounting(textContent, platforms, 'characters'),
    [textContent, platforms]
  );
  const { mostRestrictiveLimit, isWithinAll } = limitCheck;

  // Don't render if no content
  if (characterCount === 0) {
    return null;
  }

  // Don't render if no platforms selected
  if (platforms.length === 0) {
    return null;
  }

  // Calculate percentage and color
  const percentage = calculatePercentage(characterCount, mostRestrictiveLimit);
  const colorClass = getPercentageColorClass(percentage);

  return (
    <>
      {showSeparator && <div className="w-px h-4 bg-gray-300 mx-0.5" />}
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild>
          <button
            type="button"
            className={`flex items-center gap-1.5 px-2 py-1 rounded transition-colors text-sm ${colorClass} ${
              open ? 'bg-gray-100' : 'hover:bg-gray-50'
            }`}
            aria-label={t('character_count_toggle', { defaultValue: 'Toggle character count details' })}
          >
            <span>
              {formatNumber(characterCount)} / {formatNumber(mostRestrictiveLimit)}
            </span>
            <span className="text-xs text-gray-400">•••</span>
          </button>
        </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={8}
          collisionPadding={16}
          className="z-popover-top w-72 bg-white border border-gray-200 rounded-lg shadow-lg p-4 space-y-3"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          {/* Character count header with progress bar */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className={`text-sm font-medium ${colorClass}`}>
                {formatNumber(characterCount)} / {formatNumber(mostRestrictiveLimit)}
              </span>
              <span className="text-xs text-gray-500">
                {Math.round(percentage)}%
              </span>
            </div>
            <ProgressBar percentage={percentage} />
          </div>

          {/* Word and hashtag counts */}
          <div className="flex items-center gap-3 text-sm text-gray-600">
            <span>
              {wordCount.toLocaleString()} {wordCount === 1 ? t('word') : t('words')}
            </span>
            {hashtagCount > 0 && (
              <>
                <span>•</span>
                <span className="flex items-center gap-1">
                  <Icon name={ICONS.TAG} size="xs" />
                  {hashtagCount} {hashtagCount === 1 ? t('hashtag') : t('hashtags')}
                </span>
              </>
            )}
          </div>

          {/* Divider */}
          <div className="border-t border-gray-200" />

          {/* Per-platform breakdown */}
          <div className="space-y-1">
            {platforms.map((platform) => (
              <PlatformStatusRow
                key={platform}
                platform={platform}
                text={textContent}
              />
            ))}
          </div>

          {/* Warning message if over any limit */}
          {!isWithinAll && (
            <>
              <div className="border-t border-gray-200" />
              <div className="flex items-start gap-2 text-xs text-danger">
                <Icon name={ICONS.CAUTION} size="sm" color="text-danger" className="flex-shrink-0 mt-0.5" />
                <span>
                  {t('over_character_limit_warning', {
                    defaultValue: 'Content exceeds the character limit for some platforms'
                  })}
                </span>
              </div>
            </>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
    </>
  );
}

export default CompactCharacterCount;
